import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { useAuthStore } from '../store/authStore';
import { getCoachPhones, addCoachPhone, removeCoachPhone, getTeam, searchUsers } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Phone, Plus, Trash2, Shield, Users, Crown, Search, UserPlus, ArrowLeft } from 'lucide-react';

export default function CoachesPage() {
  const navigate = useNavigate();
  const { currentTeamId } = useGameStore();
  const { user } = useAuthStore();
  const [team, setTeam] = useState(null);
  const [coachData, setCoachData] = useState({ 
    master_coach_phone: '', 
    master_coach_username: null,
    coach_phones: [],
    coach_usernames: {}
  });
  const [newPhone, setNewPhone] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMasterCoach, setIsMasterCoach] = useState(false);

  useEffect(() => {
    if (!currentTeamId) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [currentTeamId]);

  useEffect(() => {
    // Search users as phone is typed
    const searchTimer = setTimeout(async () => {
      if (newPhone.length >= 3) {
        setSearching(true);
        try {
          const results = await searchUsers(newPhone);
          // Filter out already added coaches and master coach
          const filtered = results.filter(u => 
            u.phone_number !== coachData.master_coach_phone &&
            !coachData.coach_phones.includes(u.phone_number)
          );
          setSearchResults(filtered);
        } catch (error) {
          console.error('Search error:', error);
        } finally {
          setSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);
    
    return () => clearTimeout(searchTimer);
  }, [newPhone, coachData]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamData, coaches] = await Promise.all([
        getTeam(currentTeamId),
        getCoachPhones(currentTeamId)
      ]);
      setTeam(teamData);
      setCoachData(coaches);
      setIsMasterCoach(coaches.master_coach_phone === user?.phone_number);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load coach data');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleAddCoach = async (phoneNumber) => {
    const phoneDigits = phoneNumber.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      await addCoachPhone(currentTeamId, phoneDigits);
      toast.success('Coach added! They now have full team access.');
      setNewPhone('');
      setSearchResults([]);
      loadData(); // Reload to get updated list with usernames
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add coach');
    }
  };

  const handleRemoveCoach = async (phone) => {
    try {
      const result = await removeCoachPhone(currentTeamId, phone);
      setCoachData({ ...coachData, coach_phones: result.coach_phones });
      toast.success('Coach removed');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove coach');
    }
  };

  const formatDisplayPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="w-12 h-12 border-4 border-green-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 p-4 md:p-8" data-testid="coaches-page">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="mb-3 -ml-2 text-stone-600 hover:text-stone-900 hover:bg-stone-200"
            data-testid="coaches-back-btn"
          >
            <ArrowLeft size={16} className="mr-1" /> Back to Home
          </Button>
          <h1 className="text-3xl font-extrabold text-green-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Users className="inline mr-2" size={28} />
            MANAGE COACHES
          </h1>
          <p className="text-stone-600">{team?.name}</p>
        </div>

        {/* Master Coach Card */}
        <Card className="border-0 shadow-md mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Crown className="text-amber-500" size={20} />
              MASTER COACH
            </CardTitle>
            <CardDescription>
              The master coach has full control and can add/remove other coaches
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-sm p-4">
              <div className="w-10 h-10 bg-amber-500 text-white rounded-full flex items-center justify-center">
                <Phone size={18} />
              </div>
              <div className="flex-1">
                <p className="font-semibold">
                  {coachData.master_coach_username || 'Unknown'}
                </p>
                <p className="text-sm text-stone-500">
                  {formatDisplayPhone(coachData.master_coach_phone)}
                </p>
                {isMasterCoach && (
                  <p className="text-sm text-amber-600 font-medium">This is you!</p>
                )}
              </div>
              <span className="bg-amber-500 text-white px-2 py-1 rounded-sm text-xs font-bold">
                OWNER
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Add Coach */}
        {isMasterCoach && (
          <Card className="border-0 shadow-md mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <UserPlus className="text-green-700" size={20} />
                ADD ASSISTANT COACH
              </CardTitle>
              <CardDescription>
                Search for registered users by phone number or enter a new phone
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                  <Input
                    placeholder="Search by phone number..."
                    value={newPhone}
                    onChange={(e) => setNewPhone(formatPhoneNumber(e.target.value))}
                    data-testid="new-coach-phone"
                    className="pl-10"
                    maxLength={14}
                  />
                </div>

                {/* Search Results */}
                {searching && (
                  <p className="text-sm text-stone-500">Searching...</p>
                )}
                
                {searchResults.length > 0 && (
                  <div className="border border-stone-200 rounded-sm overflow-hidden">
                    <p className="text-xs text-stone-500 bg-stone-100 px-3 py-2 border-b">
                      Registered Users Found
                    </p>
                    {searchResults.map((result) => (
                      <div 
                        key={result.id}
                        className="flex items-center justify-between p-3 hover:bg-stone-50 border-b last:border-b-0"
                      >
                        <div>
                          <p className="font-medium">{result.username}</p>
                          <p className="text-sm text-stone-500">{formatDisplayPhone(result.phone_number)}</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleAddCoach(result.phone_number)}
                          data-testid={`add-user-${result.id}`}
                          className="bg-green-900 hover:bg-green-800"
                        >
                          <Plus size={14} className="mr-1" /> Add
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Manual add if no results or partial phone */}
                {newPhone.replace(/\D/g, '').length === 10 && searchResults.length === 0 && !searching && (
                  <div className="border border-dashed border-stone-300 rounded-sm p-4 text-center">
                    <p className="text-sm text-stone-600 mb-2">
                      No registered user found with this phone number.
                    </p>
                    <Button
                      onClick={() => handleAddCoach(newPhone)}
                      data-testid="add-coach-btn"
                      className="bg-green-900 hover:bg-green-800"
                    >
                      <Plus size={14} className="mr-1" /> Add Anyway
                    </Button>
                    <p className="text-xs text-stone-500 mt-2">
                      They'll get access when they register with this phone number
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Other Coaches */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Shield className="text-green-700" size={20} />
              ASSISTANT COACHES ({coachData.coach_phones.length})
            </CardTitle>
            <CardDescription>
              These users have full access to manage the team
            </CardDescription>
          </CardHeader>
          <CardContent>
            {coachData.coach_phones.length === 0 ? (
              <p className="text-center text-stone-500 py-8">
                No assistant coaches added yet.
                {isMasterCoach && ' Search for users above to add them.'}
              </p>
            ) : (
              <div className="space-y-2">
                {coachData.coach_phones.map((phone, idx) => {
                  const username = coachData.coach_usernames?.[phone];
                  return (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-sm p-3"
                      data-testid={`coach-item-${idx}`}
                    >
                      <div className="w-8 h-8 bg-green-700 text-white rounded-full flex items-center justify-center">
                        <Phone size={14} />
                      </div>
                      <div className="flex-1">
                        {username ? (
                          <>
                            <p className="font-semibold">{username}</p>
                            <p className="text-sm text-stone-500">{formatDisplayPhone(phone)}</p>
                          </>
                        ) : (
                          <>
                            <p className="font-semibold">{formatDisplayPhone(phone)}</p>
                            <p className="text-sm text-amber-600">Not yet registered</p>
                          </>
                        )}
                        {phone === user?.phone_number && (
                          <p className="text-sm text-green-600 font-medium">This is you!</p>
                        )}
                      </div>
                      {isMasterCoach && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveCoach(phone)}
                          data-testid={`remove-coach-${idx}`}
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {!isMasterCoach && (
          <p className="text-center text-stone-500 text-sm mt-4">
            Only the master coach can add or remove coaches.
          </p>
        )}

        {/* Done button at bottom for easy mobile exit */}
        <div className="mt-6 mb-2">
          <Button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-green-900 hover:bg-green-800 text-white font-bold h-12"
            data-testid="coaches-done-btn"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
